/**
 * v9.253 — Map the anatomical SVG's per-muscle labels to our coarser
 * BodyRegion taxonomy.
 *
 * The purchased anatomy SVGs (public/anatomy/front.svg + back.svg) tag
 * each muscle <g> with `data-muscle="<inkscape:label>"` (rewritten in
 * the pre-svgo pre-process step). The labels are anatomically precise
 * — "Vastus lateralis", "Brachioradialis", "Sternocleidomastoid",
 * "Pectoralis major" — far finer than our exercise-tagging taxonomy
 * (`quads`, `forearms`, `chest`, etc.).
 *
 * This map collapses many SVG labels onto one BodyRegion key so the
 * heatmap renders meaningfully against the data we actually have. The
 * BodyRegion type lives in `@/lib/volume-by-muscle`; this file is the
 * single source of truth for "which anatomical muscle paints which
 * region of our taxonomy."
 *
 * ## Rules used to assign labels
 *
 *   - Quad heads → "quads" (vastus medialis/lateralis, rectus
 *     femoris, sartorius). Sartorius stays here (hip flexor /
 *     anterior thigh, not an adductor).
 *   - v9.269 — adductors → "adductors" (their own region). "Adductor
 *     longus and pectineus" (front) + "Adductor magnus" (back) both
 *     paint it. Previously folded into quads / hamstrings; split out
 *     after a coach taxonomy review (see docs/muscle-group-taxonomy).
 *   - Hamstring heads → "hamstrings" (biceps femoris, semitendinosus
 *     ×2 — the SVG has two with different capitalisation; both
 *     belong here).
 *   - Glute heads + tensor fasciae latae → "glutes" (TFL anatomically
 *     a hip flexor but the user thinks of it as glute-area when they
 *     see the SVG).
 *   - Calf heads → "calves" (gastrocnemius medial/lateral, soleus,
 *     extensor digitorum longus = anterior shin, grouped here for
 *     the user-facing "calves" region).
 *   - Pectoralis major → "chest" — the SVG has no clavicular/
 *     sternal split, so all 3 muscle_group chest sub-buckets
 *     (chest_upper / chest / chest_lower) roll up here. v9.266
 *     collapsed the prior 3-region split (chest_upper / chest_mid /
 *     chest_lower) when user-trained chest_upper / chest_lower
 *     volume kept disappearing from the heatmap. Re-expand if a
 *     future SVG asset adds chest band paths.
 *   - Latissimus dorsi → "lats".
 *   - Thoracolumbar fascia → "lower_back" (the sheet of fascia
 *     covers the spinal erectors anatomically; that's what users
 *     mean by "lower back").
 *   - Trapezius (upper/middle/lower) + Infraspinatus + Teres major
 *     → "upper_back". Rhomboids aren't in the SVG (covered by
 *     trapezius visually). Infraspinatus + teres major are scapular
 *     stabilisers but read as "upper back" to a lifter eyeing the
 *     heatmap.
 *   - Deltoids split front/rear (v9.270): the front figure's "Deltoids
 *     (front)" path → front_delts (anterior + lateral, push); the back
 *     figure's "Deltoids (rear)" path → rear_delts (posterior, pull).
 *   - Biceps brachii + Brachialis → "biceps". Brachialis is anatomic-
 *     ally between biceps and triceps but lifters tag it with biceps
 *     for hypertrophy intent.
 *   - All Triceps brachii heads → "triceps".
 *   - Brachioradialis + Flexor/Extensor carpi (radialis/ulnaris) →
 *     "forearms". Brachioradialis is technically forearm by origin
 *     even though it functions as an elbow flexor.
 *   - Rectus abdominis (both bands) + Serratus anterior → "core".
 *     Serratus is debatable (some lifters tag it under shoulders/
 *     chest) but reads fine on "core".
 *   - v9.270 — External obliques → "obliques" (their own region,
 *     split from core after the coach taxonomy review; rotational /
 *     side-bend exercises paint here).
 *   - v9.269 — Sternocleidomastoid + Omohyoid → "neck" (their own
 *     region, promoted from null after the coach taxonomy review;
 *     the 5 seed neck-resistance exercises now paint here).
 *   - Outline + Group → null (these are container groups, not
 *     muscles).
 *
 * ## Why a runtime map (not codegen'd into the SVG)
 *
 * Keeping the assignment as a TypeScript const means changes ship
 * with code review + typecheck. The label strings are EXACTLY the
 * Inkscape labels from the source SVGs — keep them in sync if the
 * designer updates the asset.
 */

import type { BodyRegion } from "@/lib/volume-by-muscle";

/** Map from the SVG's `data-muscle` value to the BodyRegion the heatmap
 *  should paint. Returns null for non-muscle groups (outline, group
 *  containers) and for muscles we don't track (neck). */
export const MUSCLE_LABEL_TO_REGION: Record<string, BodyRegion | null> = {
  // ────── Container groups (no painting) ──────
  Group: null,
  Outline: null,

  // ────── Front view ──────
  // v9.266 — was "chest_mid"; renamed to "chest" alongside the
  // BODY_REGIONS collapse. The SVG has only one Pectoralis major
  // group so all chest sub-bucket training paints here.
  "Pectoralis major": "chest",
  // v9.270 — delts split front/rear. The front figure's Deltoid path (anterior
  // + lateral, push-trained) paints front_delts; the back figure's
  // ("Deltoids (rear)" below) paints rear_delts. SVG data-muscle attrs renamed
  // to match (migration 0121).
  "Deltoids (front)": "front_delts",
  "Biceps brachii": "biceps",
  Brachialis: "biceps",
  "Triceps brachii, long head": "triceps",
  "Triceps brachii, medial head": "triceps",
  Brachioradialis: "forearms",
  "Flexor carpi radialis": "forearms",
  "Extensor carpi radialis": "forearms",
  "Rectus abdominis": "core",
  "Rectus abdominis (lower)": "core",
  // v9.270 split obliques into their own region. The asset draws the
  // lateral wall as TWO stacked sheets that abut with no gap: "Serratus
  // anterior" (the wide upper-lateral sub-pec/rib sheet, ~y906-1188) and
  // "External obliques" (the lower love-handle band, ~y1173-1345). BOTH map
  // to obliques so the flank highlights as one continuous band from the ribs
  // to the hips. Serratus isn't directly trained (no ab/oblique exercise
  // isolates it), so per coaching guidance it reads as part of the obliques
  // rather than bleeding onto core or being its own (perpetually-empty)
  // region. The narrow central rectus columns are the abs ("core").
  "External obliques": "obliques",
  "Serratus anterior": "obliques",
  "Vastus medialis": "quads",
  "Vastus lateralis": "quads",
  "Rectus femoris": "quads",
  Sartorius: "quads",
  // v9.269 — adductors split into their own region (was "quads").
  // Sartorius stays in quads (it's a hip flexor / anterior-thigh
  // muscle, not an adductor).
  "Adductor longus and pectineus": "adductors",
  "Tensor fasciae latae": "glutes",
  Soleus: "calves",
  "extensor digitorum longus": "calves",
  // v9.269 — neck promoted to its own region (was null). Both
  // front-view neck labels paint it.
  Sternocleidomastoid: "neck",
  Omohyoid: "neck",
  // Trapezius on the FRONT view (the upper-trap part visible above the
  // collarbone) — still paints as upper_back so the user reads the
  // muscle they're actually training when they look at both views.
  Trapezius: "upper_back",

  // ────── Back view ──────
  // v9.270 — rear-delt half of the shoulder split (posterior, pull-trained);
  // pairs with "Deltoids (front)" in the front-view section above.
  "Deltoids (rear)": "rear_delts",
  "Latissimus dorsi": "lats",
  "Thoracolumbar fascia": "lower_back",
  "Trapezius (Upper/Middle focus)": "upper_back",
  "Lower trapezius": "upper_back",
  Infraspinatus: "upper_back",
  "Teres major": "upper_back",
  "Triceps brachii, long head, lateral head and medial head": "triceps",
  "Flexor carpi ulnaris": "forearms",
  "Gluteus maximus": "glutes",
  "Gluteus medius": "glutes",
  "Biceps femoris": "hamstrings",
  Semitendinosus: "hamstrings",
  // SVG has two semitendinosus groups (one lowercase) — defensive
  // dupe entry so both paint.
  semitendinosus: "hamstrings",
  // v9.269 — adductor magnus joins the new adductors region (was
  // "hamstrings"). It's the posterior adductor; pairing it with the
  // front adductor path gives the region a path on both views.
  "Adductor magnus": "adductors",
  "Gastrocnemius, medial head": "calves",
  "Gastrocnemius, lateral head": "calves",
};

/** Lookup with safe fallback. Returns null for unknown labels (forward
 *  compat — if the designer adds a new muscle and the map isn't
 *  updated, the muscle stays neutral instead of crashing the render). */
export function regionForMuscleLabel(label: string): BodyRegion | null {
  return MUSCLE_LABEL_TO_REGION[label] ?? null;
}
